import _ from "lodash";
import React, { useState } from "react";
import classes from "./AttachmentActionsModal.scss";
import { Trans } from "react-i18next";
import { Modal, Dialog, Button, TextInput, Tooltip } from "@toddle-edu/ds-web";
import { Sentry } from "services/sentry";
import ACLStore from "lib/aclStore";
import classNames from "classnames";
import {
  SHIFT_ENTER_SUBMIT_SHORTCUT,
  ATTACHMENT_TYPE_LINK,
} from "Constants/stringConstants";
import {
  ClockOutlined,
  PlaceholderFileOutlined,
  SinglePointRubricOutlined,
  UserTickOutlined,
} from "@toddle-edu/ds-icons";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { formatBytes, formatDate } from "Utils";

const AttachmentActionsModal = ({
  onClose,
  t,
  deleteLabel,
  deleteButtonText,
  cancelButtonText,
  currentDialogAction,
  currentDialgItem,
  saveRenamedAttachment,
  deleteAttachment,
  dsVersion,
  testId,
  callDeleteCallbackOnConfirm,
  onDeleteAttachmentCallback,
  parentId,
  parentType,
}) => {
  const {
    createdBy,
    name: attachmentName,
    createdAt,
    metadata,
    type,
  } = currentDialgItem;
  const { size: attachmentSize } = metadata || {};
  const isLinkAttachment = type === ATTACHMENT_TYPE_LINK;
  const DIALOGS_INFO = {
    DELETE: {
      title: t("common:delete_with_label", {
        label: deleteLabel,
      }),
      message: "common:delete_msg_with_label_multiline",
      confirmButtonText: deleteButtonText,
      cancelButtonText: cancelButtonText,
      onClickButton2: () => {
        const attachmentId = _.get(currentDialgItem, "id", "");
        if (
          !attachmentId &&
          ACLStore.can("FeatureFlag:ProjectsSentryDebugLog")
        ) {
          // eslint-disable-next-line @toddle-edu/common/no-sentry-capture-exception
          Sentry.captureException(
            new Error("PROJECTS_SUBMISSION_FILE_DELETE_ERROR"),
            {
              level: "info",
              extra: {
                currentDialgItem,
              },
            }
          );
        }
        deleteAttachment(attachmentId, true);
        onClose();
        if (
          callDeleteCallbackOnConfirm &&
          _.isFunction(onDeleteAttachmentCallback)
        ) {
          onDeleteAttachmentCallback({ attachmentId, parentId, parentType });
        }
      },
    },
    RENAME: {
      title: t("common:rename_label_lowercase", { label: t("common:file") }),
      confirmButtonText: "common:rename",
      cancelButtonText: "common:cancel",
      onClickButton2: newName => saveRenamedAttachment(newName),
      isPromptBox: true,
      promptDetail: ({ name, t }) => ({
        placeholder: t("common:file_name"),
        label: t("common:file_name"),
        checkValidation: true,
        promptText: name,
      }),
    },
    DETAILS: {
      title: t("project:submission_details"),
      confirmButtonText: "common:done",
      body: (
        <div className={classes.detailsContainer}>
          <div className={classes.leftDetailsContainer}>
            <div className={classes.detailLeftItem}>
              <PlaceholderFileOutlined size="xxx-small" />
              <span className="text-body"> {t("common:file_name")}</span>
            </div>
            <div className={classes.detailLeftItem}>
              <UserTickOutlined size="xxx-small" />
              <span className="text-body"> {t("common:added_by")}</span>
            </div>
            <div className={classes.detailLeftItem}>
              <ClockOutlined size="xxx-small" />
              <span className="text-body"> {t("common:added_on")}</span>
            </div>
            {!isLinkAttachment && (
              <div className={classes.detailLeftItem}>
                <SinglePointRubricOutlined size="xxx-small" />
                <span className="text-body"> {t("common:file_size")}</span>
              </div>
            )}
          </div>
          <div className={classes.rightDetailsContainer}>
            <div className={classes.detailRightItem}>
              <Tooltip
                dsVersion="2.0"
                tooltip={
                  <div className={classes.tooltip}>{attachmentName}</div>
                }
              >
                <span
                  className={classNames("text-body", classes.attachmentName)}
                >
                  {attachmentName}
                </span>
              </Tooltip>
            </div>
            <div className={classes.detailRightItem}>
              <span className="text-body">
                {getFormattedUserName({ user: createdBy })}
              </span>
            </div>

            <div className={classes.detailRightItem}>
              <span className="text-body">
                {formatDate({
                  date: `${createdAt}z`,
                  format: DATE_TIME_CONSTANTS.D_MMM_YYYY_h_mm_a,
                })}
              </span>
            </div>
            {!isLinkAttachment && (
              <div className={classes.detailRightItem}>
                <span className="text-body">
                  {formatBytes({ bytes: attachmentSize })}
                </span>
              </div>
            )}
          </div>
        </div>
      ),
    },
  };

  const { placeholder, promptText, label } =
    (DIALOGS_INFO[currentDialogAction].promptDetail &&
      DIALOGS_INFO[currentDialogAction].promptDetail({
        name: _.trim(_.get(currentDialgItem, "name", "")),
        t,
      })) ||
    {};
  const [fileName, setFileName] = useState(promptText);

  const handleRenameFile = event => {
    setFileName(event.target.value);
  };

  const isDsV2 = dsVersion === "2.0";

  const getModal = ({ currentDialogAction }) => {
    switch (currentDialogAction) {
      case "DELETE": {
        return (
          <Dialog
            title={DIALOGS_INFO[currentDialogAction].title}
            onClose={onClose}
            buttonOneText={
              t(DIALOGS_INFO[currentDialogAction].cancelButtonText) ||
              t("common:cancel")
            }
            buttonTwoText={t(
              DIALOGS_INFO[currentDialogAction].confirmButtonText
            )}
            onButtonOneClick={onClose}
            onButtonTwoClick={e => {
              DIALOGS_INFO[currentDialogAction].onClickButton2(e);
            }}
            message={
              <Trans i18nKey={DIALOGS_INFO[currentDialogAction].message}>
                {{
                  label: deleteLabel,
                }}
                <div></div>
              </Trans>
            }
            dsVersion={dsVersion}
          />
        );
      }
      case "RENAME": {
        const footerClasses = classNames({
          [classes.footer]: true,
          [classes.footerV2]: isDsV2,
        });
        return (
          <Modal
            height="fit-content"
            width={isDsV2 ? "480px" : "500px"}
            onClose={onClose}
            dsVersion={dsVersion}
          >
            <Modal.Header
              title={DIALOGS_INFO[currentDialogAction].title}
              testId={testId}
            />
            <Modal.Body>
              <TextInput
                value={fileName}
                label={label}
                placeholder={placeholder}
                required
                onChange={handleRenameFile}
                size="large"
                dsVersion={dsVersion}
                testId={`${testId}-rename`}
              />
            </Modal.Body>
            <Modal.Footer>
              <div className={footerClasses}>
                <Button
                  onClick={onClose}
                  variant={isDsV2 ? "neutral" : "outlined-subtle"}
                  type={isDsV2 ? "outlined" : ""}
                  size={isDsV2 ? "medium" : "large"}
                  dsVersion={dsVersion}
                  testId={`${testId}-cancel`}
                >
                  {t(DIALOGS_INFO[currentDialogAction].cancelButtonText) ||
                    t("common:cancel")}
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    DIALOGS_INFO[currentDialogAction].onClickButton2(fileName);
                  }}
                  disabled={!fileName}
                  size={isDsV2 ? "medium" : "large"}
                  variant={isDsV2 ? "primary" : "progressive"}
                  type={isDsV2 ? "fill" : ""}
                  shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
                  dsVersion={dsVersion}
                  testId={`${testId}-rename`}
                >
                  {t(DIALOGS_INFO[currentDialogAction].confirmButtonText)}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        );
      }
      case "DETAILS": {
        return (
          <Modal
            dsVersion={dsVersion}
            onClose={onClose}
            height="fit-content"
            width="480px"
          >
            <Modal.Header
              hasSeparator={true}
              style={{ padding: "12px 24px" }}
              title={DIALOGS_INFO[currentDialogAction].title}
              testId={testId}
            />
            <Modal.Body>{DIALOGS_INFO[currentDialogAction].body}</Modal.Body>
            <Modal.Footer
              hasSeparator={true}
              style={{
                padding: "12px 24px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={onClose}
                variant="primary"
                size="medium"
                type="fill"
                dsVersion={dsVersion}
                testId={`${testId}-done`}
              >
                {t(DIALOGS_INFO[currentDialogAction].confirmButtonText)}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      }
      default: {
        break;
      }
    }
  };
  return <>{getModal({ currentDialogAction })}</>;
};

export default AttachmentActionsModal;
